import React, { useCallback } from 'react'

import { useRouter } from 'next/router'

import { AnnouncementModal } from '@/components/announcements/AnnouncementModal'
import { ANNOUNCEMENT_REGISTRY } from '@/components/announcements/registry'

import { useKnockReady } from '@/notifications/knock/components/NotificationProvider'
import { useAnnouncementQueue } from '@/notifications/knock/hooks/useAnnouncementQueue'

// Enabled in local dev and Vercel preview deploys, never in real production, so
// the review hatch below can't be triggered by end users. (Vercel preview
// builds run as NODE_ENV=production, hence the extra check; if the Vercel var
// isn't exposed it's simply undefined and this stays dev-only.)
const IS_PREVIEW_ENVIRONMENT =
  process.env.NODE_ENV !== 'production' ||
  process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'

// Preview hatch: `?previewAnnouncement=<entry_id>` force-opens a registered
// modal so the UI can be reviewed without publishing to Knock.
const usePreviewEntryId = (): string | null => {
  const router = useRouter()
  if (!IS_PREVIEW_ENVIRONMENT) return null
  const raw = router.query.previewAnnouncement
  if (typeof raw !== 'string') return null
  return raw in ANNOUNCEMENT_REGISTRY ? raw : null
}

const AnnouncementModalHostInner: React.FC = () => {
  const router = useRouter()
  const { current, dismiss } = useAnnouncementQueue()
  const previewEntryId = usePreviewEntryId()

  const handlePreviewDismiss = useCallback(() => {
    const nextQuery = { ...router.query }
    delete nextQuery.previewAnnouncement
    void router.replace(
      { pathname: router.pathname, query: nextQuery },
      undefined,
      { shallow: true },
    )
  }, [router])

  if (previewEntryId !== null) {
    const PreviewBody = ANNOUNCEMENT_REGISTRY[previewEntryId].Body
    return (
      <AnnouncementModal open onDismiss={handlePreviewDismiss}>
        <PreviewBody
          data={{
            entry_id: previewEntryId,
            changelog_url: `/changelog?entry=${previewEntryId}`,
          }}
          onDismiss={handlePreviewDismiss}
        />
      </AnnouncementModal>
    )
  }

  if (current === null) return null

  const Body = current.config.Body
  return (
    <AnnouncementModal open onDismiss={dismiss}>
      <Body data={current.data} onDismiss={dismiss} />
    </AnnouncementModal>
  )
}

/**
 * Mounted once, app-wide, inside the Knock provider. Shows a forced modal for
 * unread, opted-in changelog announcements (durable via Knock read state).
 * Renders nothing until Knock is ready or when there's nothing to show.
 */
export const AnnouncementModalHost: React.FC = () => {
  const isReady = useKnockReady()
  if (!isReady) return null
  return <AnnouncementModalHostInner />
}
